/**
 * Custom hook for managing PO Lines Tab state and operations.
 * Composes sub-hooks for bulk actions, export, and workflow actions.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { purchasingAPI, PurchaseOrderLine, authAPI } from '../../../../lib/api';
import { getApiErrorMessage } from '../../../../lib/api/types/common';
import { Cache } from '../../../../lib/cache';
import { useAuth } from '../../../../contexts/AuthContext';
import { useView } from '../../../../contexts/ViewContext';
import {
  ColumnConfig,
  DEFAULT_COLUMNS,
  COLUMN_STORAGE_KEY,
  COLUMN_VERSION_STORAGE_KEY,
  DEFAULT_COLUMNS_VERSION,
  CUSTOMER_RESTRICTED_COLUMNS,
  POLinesFilters,
  DEFAULT_FILTERS,
  FilterOptions,
} from './types';
import { usePOLinesBulkActions } from './usePOLinesBulkActions';
import { usePOLinesExport } from './usePOLinesExport';
import { usePOLinesActions } from './usePOLinesActions';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useIsCustomer } from '../../../../hooks/usePortalMode';

// Columns hidden from PO Lines when schedule_collaboration is enabled.
// These are workflow-action columns whose interactions live at the
// schedule level. Supplier-portal keeps acknowledge_status and hold_reason
// visible at the PO-line level — they're read-only data columns the
// Cardiff Open Orders view depends on, even with schedule_collaboration
// turned on. Previously the list included those two; their auto-hide
// was hiding them on every refresh regardless of the user's column-picker
// preference, blocking the Ack Status restoration after the upstream sync.
const SCHEDULE_COLLAB_HIDDEN_COLUMNS = [
  'action', 'next_action', 'on_hold',
];

const DEFAULT_PAGE_SIZE = 50;

// Local (not UTC) calendar date as YYYY-MM-DD, for the "Due today" quick filter.
// Avoids toISOString (UTC roll-over) and toLocaleDateString (ESLint-banned).
const getLocalToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function usePOLinesState() {
  const { user } = useAuth();
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // CR-4695 rework: Default to 'open' so closed lines aren't shown by default
  const [activeTab, setActiveTab] = useState('open');
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Selection state
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [selectedLineAction, setSelectedLineAction] = useState<'propose_change' | null>(null);

  // Sort state
  // Default sort: Supplier Promise Date ascending — shows what's due first
  // (soonest / most-overdue at the top), undated lines pushed to the bottom
  // (NullsLastOrderingFilter). Across all tabs.
  const [sortField, setSortField] = useState<string | null>('supplier_promise_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Tab badge counts
  const [needsActionCount, setNeedsActionCount] = useState<number | null>(null);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [closedCount, setClosedCount] = useState<number | null>(null);
  const [onHoldCount, setOnHoldCount] = useState<number | null>(null);
  const [completedCount, setCompletedCount] = useState<number | null>(null);
  const [dueTodayCount, setDueTodayCount] = useState<number | null>(null);
  const [highPriorityCount, setHighPriorityCount] = useState<number | null>(null);

  // Filter options (CR-3413)
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Filter state
  const [filters, setFilters] = useState<POLinesFilters>(DEFAULT_FILTERS);

  // Column visibility and order state (CR-3406, CR-3846)
  // When schedule_collaboration is enabled, hide ack/action columns (managed at schedule level)
  // CR-4716: Customer users cannot see commercial/internal columns at all
  const isCustomerUser = useIsCustomer();
  // Columns force-hidden for every user regardless of any localStorage
  // preference. Add IDs here to retire a column across the fleet — this
  // overrides savedCol.visible in the merge step below.
  const ALWAYS_HIDDEN_COLUMNS = ['action', 'on_hold'];
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const hideCollab = (cols: ColumnConfig[]) =>
      scheduleCollabEnabled
        ? cols.map(c => SCHEDULE_COLLAB_HIDDEN_COLUMNS.includes(c.id) ? { ...c, visible: false } : c)
        : cols;
    const forceHide = (cols: ColumnConfig[]) =>
      cols.map(c => ALWAYS_HIDDEN_COLUMNS.includes(c.id) ? { ...c, visible: false } : c);

    // CR-4716: Remove customer-restricted columns entirely for customer users
    const stripRestricted = (cols: ColumnConfig[]) =>
      isCustomerUser ? cols.filter(c => !CUSTOMER_RESTRICTED_COLUMNS.includes(c.id)) : cols;

    const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
    const savedVersionRaw = localStorage.getItem(COLUMN_VERSION_STORAGE_KEY);
    const savedVersion = savedVersionRaw ? parseInt(savedVersionRaw, 10) : 0;
    // Discard any saved layout from before the current defaults version —
    // forces a one-time reset to DEFAULT_COLUMNS for everyone after a bump.
    if (saved && savedVersion >= DEFAULT_COLUMNS_VERSION) {
      try {
        const parsed = JSON.parse(saved);
        const merged = DEFAULT_COLUMNS.map(defaultCol => {
          const savedCol = parsed.find((c: ColumnConfig) => c.id === defaultCol.id);
          return savedCol
            ? { ...defaultCol, visible: savedCol.visible, order: savedCol.order ?? defaultCol.order }
            : defaultCol;
        }).sort((a, b) => a.order - b.order);
        return stripRestricted(forceHide(hideCollab(merged)));
      } catch {
        return stripRestricted(forceHide(hideCollab(DEFAULT_COLUMNS)));
      }
    }
    return stripRestricted(forceHide(hideCollab(DEFAULT_COLUMNS)));
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  // Track whether the next fetch should bust the browser HTTP cache.
  const bustCacheRef = useRef(false);

  // CR-3383: Check for PO number and line number filter from URL params
  // Wait for ViewContext to finish loading before marking ready
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (viewContextLoading) return;
    initializedRef.current = true;

    const urlPoFilter = searchParams.get('poFilter');
    const urlLineFilter = searchParams.get('lineFilter');
    const urlSearch = searchParams.get('search');
    // CR-4667: Customer id passed from OTIF drill-down to disambiguate
    // POs whose number collides across customers.
    const urlCustomer = searchParams.get('customer');

    if (urlPoFilter) {
      setFilters(prev => ({
        ...prev,
        poNumber: urlPoFilter,
        lineNumber: urlLineFilter || '',
        customer: urlCustomer || prev.customer,
      }));
      setActiveTab('all');
      setShowFilters(true);
      setSearchParams({}, { replace: true });
    } else if (urlSearch) {
      setFilters(prev => ({
        ...prev,
        poNumber: urlSearch,
        customer: urlCustomer || prev.customer,
      }));
      setShowFilters(true);
      setSearchParams({}, { replace: true });
    } else if (urlCustomer) {
      setFilters(prev => ({ ...prev, customer: urlCustomer }));
      setShowFilters(true);
      setSearchParams({}, { replace: true });
    }
    setInitialLoadComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading]);

  // Fetch lines from API
  const fetchLines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const viewParams = getViewQueryParams();
      // Default sort: Latest Customer Promise Date ascending (earliest =
      // most urgent first). NULLs sort last in Postgres ASC, so lines
      // without a promise date drop to the bottom. Users can override
      // by clicking column headers.
      // Closed tab is historical, so sort by Order Date descending (most recently ordered first).
      const defaultOrdering = activeTab === 'closed' ? '-po__issue_date' : 'supplier_promise_date';
      const orderingParam = sortField
        ? `${sortDirection === 'desc' ? '-' : ''}${sortField}`
        : defaultOrdering;
      const queryParams: Record<string, string | number | boolean> = {
        ordering: orderingParam,
        page: currentPage,
        page_size: pageSize,
        ...viewParams,
      };

      // Only bust the browser's 60-second HTTP cache after mutations
      if (bustCacheRef.current) {
        queryParams['_t'] = Date.now();
        bustCacheRef.current = false;
      }

      // Apply filters
      if (filters.poNumber) queryParams['po__po_number__icontains'] = filters.poNumber;
      if (filters.lineNumber) queryParams['line_number'] = filters.lineNumber;
      if (filters.customer) queryParams['po__customer'] = filters.customer;
      else if (filters.customerSearch) queryParams['po__customer__name__icontains'] = filters.customerSearch;
      if (filters.supplier) queryParams['po__supplier'] = filters.supplier;
      if (filters.partNumber) queryParams['part_number__icontains'] = filters.partNumber;
      if (filters.description) queryParams['description__icontains'] = filters.description;
      // Status filter mirrors the visible "Status" column. That column overlays
      // "On Hold" (the on_hold flag) on top of the DNA processing_stage, so a
      // held line reads "On Hold" no matter its stage. Therefore 'on_hold' maps
      // to the flag (processing_stage is NEVER 'on_hold' in the data), and the
      // real stages additionally exclude held lines so the result matches what
      // the grid renders. The dedicated On Hold filter below overrides.
      if (filters.status === 'on_hold') {
        queryParams['on_hold'] = true;
      } else if (filters.status) {
        queryParams['processing_stage'] = filters.status;
        queryParams['on_hold'] = false;
      }
      if (filters.on_hold === 'yes') queryParams['on_hold'] = true;
      else if (filters.on_hold === 'no') queryParams['on_hold'] = false;
      if (filters.buyerId) queryParams['buyer_id__icontains'] = filters.buyerId;
      if (filters.batchSerialNo) queryParams['batch_serial_no__icontains'] = filters.batchSerialNo;
      if (filters.otifOverridden === 'yes') queryParams['otif_overridden'] = true;
      else if (filters.otifOverridden === 'no') queryParams['otif_overridden'] = false;
      if (filters.customerGroup) queryParams['po__customer__group__name__icontains'] = filters.customerGroup;
      // CR-4693 rework: Use delivery_date (the visible ODD column) not sla_delivery_date
      // which is often null. Users filter by what they see in the table.
      if (filters.oddFrom) queryParams['delivery_date__gte'] = filters.oddFrom;
      if (filters.oddTo) queryParams['delivery_date__lte'] = filters.oddTo;
      if (filters.ppv === 'positive') queryParams['ppv__gt'] = 0;
      else if (filters.ppv === 'negative') queryParams['ppv__lt'] = 0;
      else if (filters.ppv === 'zero') queryParams['ppv'] = 0;
      if (filters.commodityGroup) queryParams['commodity_group'] = filters.commodityGroup;
      if (filters.commodityType) queryParams['commodity_type'] = filters.commodityType;

      // Tab-based filtering (quick-filter views). Open/Closed/On-Hold follow
      // the VISIBLE Status column (on_hold overlaid on processing_stage), not
      // receipt status:
      //   Open    = On Hold / Docked / WIP  -> open_view=true (backend OR)
      //   Closed  = Completed / Shipped (not on hold)
      //   On Hold = the on_hold flag
      if (activeTab === 'open') queryParams['open_view'] = true;
      else if (activeTab === 'closed') {
        queryParams['on_hold'] = false;
        queryParams['processing_stage__in'] = 'completed,shipped';
      }
      else if (activeTab === 'on_hold') {
        // On Hold tab = the on_hold flag. A held line renders "On Hold" in the
        // grid regardless of its underlying DNA stage, so the flag alone is the
        // correct, view-consistent definition.
        queryParams['on_hold'] = true;
      }
      else if (activeTab === 'completed') {
        // Completed tab = visible "Completed" status only (the Closed tab is
        // the wider Completed+Shipped set). Held lines render "On Hold", so
        // exclude them.
        queryParams['on_hold'] = false;
        queryParams['processing_stage'] = 'completed';
      }
      else if (activeTab === 'needs_action') queryParams['needs_action'] = true;
      else if (activeTab === 'due_today') {
        // Open lines whose visible Customer Promise Date
        // (supplier_promise_date) is today.
        queryParams['open_view'] = true;
        queryParams['supplier_promise_date'] = getLocalToday();
      }
      else if (activeTab === 'high_priority') {
        // Open lines flagged high OR critical priority (the quick filter
        // surfaces everything above "medium", not just the literal 'high').
        queryParams['open_view'] = true;
        queryParams['priority__in'] = 'high,critical';
      }

      const response = await purchasingAPI.getLines(queryParams);
      setLines(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));

      if (activeTab === 'needs_action') {
        setNeedsActionCount(response.data.count);
      }
    } catch (err: unknown) {
      console.error('Error fetching PO lines:', err);
      setError(getApiErrorMessage(err, 'Failed to load PO lines'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, activeTab, filters, getViewQueryParams]);

  // Fetch on page change, tab change, search trigger, or view context change
  const fetchCountRef = useRef(0);
  const prevActiveTabRef = useRef(activeTab);
  const prevPageRef = useRef(currentPage);
  const prevViewRef = useRef(`${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`);
  const prevSearchTriggerRef = useRef(searchTrigger);
  const prevPageSizeRef = useRef(pageSize);
  useEffect(() => {
    if (!initialLoadComplete) return;

    fetchCountRef.current += 1;
    const tabChanged = prevActiveTabRef.current !== activeTab;
    const pageChanged = prevPageRef.current !== currentPage;
    const currentViewKey = `${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`;
    const viewChanged = prevViewRef.current !== currentViewKey;
    const searchTriggered = prevSearchTriggerRef.current !== searchTrigger;
    const pageSizeChanged = prevPageSizeRef.current !== pageSize;
    prevActiveTabRef.current = activeTab;
    prevPageRef.current = currentPage;
    prevViewRef.current = currentViewKey;
    prevSearchTriggerRef.current = searchTrigger;
    prevPageSizeRef.current = pageSize;

    if (fetchCountRef.current === 2 && !tabChanged && !pageChanged && !viewChanged && !searchTriggered && !pageSizeChanged) return;

    fetchLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab, initialLoadComplete, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, searchTrigger, pageSize]);

  // CR-4597: Build filter params matching the main fetch query (shared logic)
  const buildFilterParams = useCallback(() => {
    const viewParams = getViewQueryParams();
    const params: Record<string, string | number | boolean> = {};
    Object.entries(viewParams).forEach(([k, v]) => { if (v !== undefined) params[k] = v; });
    if (filters.poNumber) params['po__po_number__icontains'] = filters.poNumber;
    if (filters.lineNumber) params['line_number'] = filters.lineNumber;
    if (filters.customer) params['po__customer'] = filters.customer;
    else if (filters.customerSearch) params['po__customer__name__icontains'] = filters.customerSearch;
    if (filters.supplier) params['po__supplier'] = filters.supplier;
    if (filters.partNumber) params['part_number__icontains'] = filters.partNumber;
    if (filters.description) params['description__icontains'] = filters.description;
    // Status filter mirrors the main query (see fetchLines): 'on_hold' targets
    // the flag, real stages target processing_stage and exclude held lines, so
    // the tab badge counts narrow exactly as the grid does.
    if (filters.status === 'on_hold') {
      params['on_hold'] = true;
    } else if (filters.status) {
      params['processing_stage'] = filters.status;
      params['on_hold'] = false;
    }
    if (filters.on_hold === 'yes') params['on_hold'] = true;
    else if (filters.on_hold === 'no') params['on_hold'] = false;
    if (filters.buyerId) params['buyer_id__icontains'] = filters.buyerId;
    if (filters.batchSerialNo) params['batch_serial_no__icontains'] = filters.batchSerialNo;
    if (filters.otifOverridden === 'yes') params['otif_overridden'] = true;
    else if (filters.otifOverridden === 'no') params['otif_overridden'] = false;
    if (filters.customerGroup) params['po__customer__group__name__icontains'] = filters.customerGroup;
    // CR-4693 rework: Match main query — use delivery_date
    if (filters.oddFrom) params['delivery_date__gte'] = filters.oddFrom;
    if (filters.oddTo) params['delivery_date__lte'] = filters.oddTo;
    if (filters.ppv === 'positive') params['ppv__gt'] = 0;
    else if (filters.ppv === 'negative') params['ppv__lt'] = 0;
    else if (filters.ppv === 'zero') params['ppv'] = 0;
    if (filters.commodityGroup) params['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) params['commodity_type'] = filters.commodityType;
    return params;
  }, [getViewQueryParams, filters]);

  // Fetch tab badge counts via the single-round-trip aggregate endpoint.
  // Deps deliberately exclude `buildFilterParams` (and therefore `filters`) —
  // we only refetch on Search click (searchTrigger), tab/page-level signals,
  // or view-context change. `buildFilterParams()` is invoked at fetch time
  // so it picks up the latest filter values without running the effect on
  // every keystroke. This keeps the badge counts in sync with what the main
  // list query is showing.
  useEffect(() => {
    if (!initialLoadComplete) return;
    purchasingAPI.getLineTabCounts(buildFilterParams())
      .then((res) => {
        setNeedsActionCount(res.data.needs_action);
        setOpenCount(res.data.open);
        setClosedCount(res.data.closed);
        setOnHoldCount(res.data.on_hold);
        setCompletedCount(res.data.completed);
        setDueTodayCount(res.data.due_today);
        setHighPriorityCount(res.data.high_priority);
      })
      .catch(() => {
        // Failed counts shouldn't break the grid; leave previous values.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadComplete, searchTrigger, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // CR-4695: Only clear view-dependent filters (supplier/customer) on site change.
  // Status, partNumber, oddFrom, oddTo and other user-selected filters are preserved.
  const prevViewKeyForFilters = useRef(`${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`);
  useEffect(() => {
    if (!initialLoadComplete) return;
    const currentKey = `${viewLevel}-${selectedSiteId}-${selectedCompanyId}-${selectedGroupId}`;
    if (prevViewKeyForFilters.current !== currentKey) {
      prevViewKeyForFilters.current = currentKey;
      setFilters(prev => ({
        ...prev,
        supplier: '',
        supplierSearch: '',
        customer: '',
        customerSearch: '',
      }));
    }
  }, [viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, initialLoadComplete]);

  // Hydrate column preferences from server on first load. Server is the
  // source of truth — overrides localStorage so the layout follows the
  // user across devices/browsers. localStorage is kept as an offline
  // fallback only.
  const prefsHydratedRef = useRef(false);
  useEffect(() => {
    if (prefsHydratedRef.current || !user?.id) return;
    prefsHydratedRef.current = true;
    authAPI.getUserPreferences()
      .then((res) => {
        const remote = (res?.data?.data?.po_line_columns
          ?? res?.data?.po_line_columns) as ColumnConfig[] | undefined;
        const remoteVersion = (res?.data?.data?.po_line_columns_version
          ?? res?.data?.po_line_columns_version) as number | undefined;
        // Server prefs missing or pre-v2: actively push the new defaults
        // up so every user's stored layout flips to the current version
        // on next load (until they customise it themselves).
        if (
          !Array.isArray(remote) || remote.length === 0
          || (remoteVersion ?? 0) < DEFAULT_COLUMNS_VERSION
        ) {
          const lean = DEFAULT_COLUMNS.map(c => ({ id: c.id, visible: c.visible, order: c.order }));
          authAPI.updateUserPreferences({
            po_line_columns: lean,
            po_line_columns_version: DEFAULT_COLUMNS_VERSION,
          }).catch(() => { /* silent */ });
          return;
        }
        const merged = DEFAULT_COLUMNS.map(defaultCol => {
          const savedCol = remote.find((c) => c.id === defaultCol.id);
          return savedCol
            ? { ...defaultCol, visible: savedCol.visible, order: savedCol.order ?? defaultCol.order }
            : defaultCol;
        }).sort((a, b) => a.order - b.order);
        setColumns(prev => {
          const hideCollab = (cols: ColumnConfig[]) =>
            scheduleCollabEnabled
              ? cols.map(c => SCHEDULE_COLLAB_HIDDEN_COLUMNS.includes(c.id) ? { ...c, visible: false } : c)
              : cols;
          const forceHide = (cols: ColumnConfig[]) =>
            cols.map(c => ALWAYS_HIDDEN_COLUMNS.includes(c.id) ? { ...c, visible: false } : c);
          const stripRestricted = (cols: ColumnConfig[]) =>
            isCustomerUser ? cols.filter(c => !CUSTOMER_RESTRICTED_COLUMNS.includes(c.id)) : cols;
          const next = stripRestricted(forceHide(hideCollab(merged)));
          return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
        });
      })
      .catch(() => { /* fall back to localStorage / defaults already in state */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Persist column preferences. Writes to localStorage immediately for
  // instant UX + a debounced PATCH to the server so the layout follows
  // the user across devices. Skip the very first run (initial mount)
  // to avoid clobbering server prefs before the hydrate effect lands.
  const firstColumnsWriteRef = useRef(true);
  useEffect(() => {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(columns));
    localStorage.setItem(COLUMN_VERSION_STORAGE_KEY, String(DEFAULT_COLUMNS_VERSION));
    if (firstColumnsWriteRef.current) {
      firstColumnsWriteRef.current = false;
      return;
    }
    if (!user?.id) return;
    const t = setTimeout(() => {
      // Strip transient/derived flags before persisting — only id/visible/order.
      const lean = columns.map(c => ({ id: c.id, visible: c.visible, order: c.order }));
      authAPI.updateUserPreferences({
        po_line_columns: lean,
        po_line_columns_version: DEFAULT_COLUMNS_VERSION,
      }).catch(() => { /* silent */ });
    }, 500);
    return () => clearTimeout(t);
  }, [columns, user?.id]);

  // Close column dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch filter options for dropdowns (CR-3413)
  useEffect(() => {
    const FILTER_CACHE_KEY = 'po_lines_filter_options';
    const cached = Cache.get<FilterOptions>(FILTER_CACHE_KEY);
    if (cached) {
      setFilterOptions(cached);
      return;
    }
    const fetchFilterOptions = async () => {
      try {
        const response = await purchasingAPI.getLineFilterOptions();
        setFilterOptions(response.data);
        Cache.set(FILTER_CACHE_KEY, response.data, 15 * 60 * 1000);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  // Reset page to 1 when switching tabs
  const handleTabChange = useCallback((tab: string) => {
    setCurrentPage(1);
    setActiveTab(tab);
  }, []);

  /** Refresh one line in the grid after a modal action without a full re-fetch. */
  const patchLine = useCallback(async (lineId: number) => {
    try {
      const response = await purchasingAPI.getLine(lineId);
      setLines((prev) => prev.map((l) => (l.id === lineId ? response.data : l)));
    } catch (err) {
      console.error('Failed to patch line:', err);
    }
  }, []);

  // Column helpers
  const toggleColumn = useCallback((columnId: string) => {
    setColumns(prev => prev.map(col =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  }, []);

  const resetColumns = useCallback(() => {
    // CR-4716: Strip restricted columns for customer users on reset too
    setColumns(isCustomerUser
      ? DEFAULT_COLUMNS.filter(c => !CUSTOMER_RESTRICTED_COLUMNS.includes(c.id))
      : DEFAULT_COLUMNS);
  }, [isCustomerUser]);

  const selectAllColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, visible: true })));
  }, []);

  const reorderColumns = useCallback((dragIndex: number, hoverIndex: number) => {
    setColumns(prev => {
      const newColumns = [...prev];
      const draggedColumn = newColumns[dragIndex];
      newColumns.splice(dragIndex, 1);
      newColumns.splice(hoverIndex, 0, draggedColumn);
      return newColumns.map((col, index) => ({ ...col, order: index }));
    });
  }, []);

  const isColumnVisible = useCallback((columnId: string): boolean => {
    const col = columns.find(c => c.id === columnId);
    return col ? col.visible : true;
  }, [columns]);

  const getColumnConfig = useCallback((columnId: string): ColumnConfig | undefined => {
    return columns.find(c => c.id === columnId);
  }, [columns]);

  // Filter handlers
  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    setSearchTrigger(prev => prev + 1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, []);

  // Page change handler
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalPages]);

  // Sort handlers
  const handleSort = useCallback((field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Compose sub-hooks
  const actions = usePOLinesActions({ fetchLines, setLines, bustCacheRef });
  const bulkActions = usePOLinesBulkActions({ lines, fetchLines, bustCacheRef });
  const exportActions = usePOLinesExport({ filters, activeTab });

  // Filter lines by nextAction (client-side, CR-3413)
  const filteredLines = filters.nextAction
    ? lines.filter(line => {
        if (filters.nextAction === 'supplier') {
          return line.waiting_on === 'Supplier';
        } else if (filters.nextAction === 'customer') {
          return line.waiting_on === 'Customer';
        }
        return true;
      })
    : lines;

  // Sort lines
  const sortedLines = [...filteredLines].sort((a, b) => {
    if (!sortField) return 0;

    let aRaw: unknown;
    let bRaw: unknown;
    if (sortField === 'pct_delivered') {
      const pctA = parseFloat(a.quantity) > 0 ? (parseFloat(a.received_qty) / parseFloat(a.quantity)) * 100 : 0;
      const pctB = parseFloat(b.quantity) > 0 ? (parseFloat(b.received_qty) / parseFloat(b.quantity)) * 100 : 0;
      aRaw = pctA;
      bRaw = pctB;
    } else if (sortField === 'next_action') {
      aRaw = a.waiting_on || '';
      bRaw = b.waiting_on || '';
    } else if (sortField === 'hold_reason') {
      aRaw = a.on_hold_reason_text || a.on_hold_reason_details?.description || '';
      bRaw = b.on_hold_reason_text || b.on_hold_reason_details?.description || '';
    } else {
      aRaw = a[sortField as keyof PurchaseOrderLine];
      bRaw = b[sortField as keyof PurchaseOrderLine];
    }

    if (aRaw === null || aRaw === undefined) return 1;
    if (bRaw === null || bRaw === undefined) return -1;

    let aVal: string | number = String(aRaw);
    let bVal: string | number = String(bRaw);

    if (['quantity', 'unit_price', 'total_price', 'received_qty', 'outstanding_qty'].includes(sortField)) {
      aVal = parseFloat(String(aRaw));
      bVal = parseFloat(String(bRaw));
    }

    if (['delivery_date', 'acknowledge_date', 'on_hold_date', 'supplier_promise_date', 'next_job_due'].includes(sortField)) {
      aVal = new Date(String(aRaw)).getTime();
      bVal = new Date(String(bRaw)).getTime();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return {
    // Feature flags
    scheduleCollabEnabled,

    // Data
    lines: sortedLines,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    needsActionCount,
    openCount,
    closedCount,
    onHoldCount,
    completedCount,
    dueTodayCount,
    highPriorityCount,
    pageSize,
    handlePageSizeChange: (newSize: number) => { setPageSize(newSize); setCurrentPage(1); },
    activeTab,
    showFilters,
    user,

    // Selection
    selectedLineId,
    selectedLineAction,
    selectedLineForHold: actions.selectedLineForHold,
    selectedLineForWorkflow: actions.selectedLineForWorkflow,
    selectedLineIds: bulkActions.selectedLineIds,
    bulkAccepting: bulkActions.bulkAccepting,
    bulkRejecting: bulkActions.bulkRejecting,
    bulkHolding: bulkActions.bulkHolding,
    showBulkAcceptModal: bulkActions.showBulkAcceptModal,
    showBulkRejectModal: bulkActions.showBulkRejectModal,
    showBulkHoldModal: bulkActions.showBulkHoldModal,

    // Modals
    putOnHoldModalOpen: actions.putOnHoldModalOpen,
    releaseHoldModalOpen: actions.releaseHoldModalOpen,
    acceptModalOpen: actions.acceptModalOpen,
    declineModalOpen: actions.declineModalOpen,
    holdReason: actions.holdReason,

    // Sort
    sortField,
    sortDirection,

    // Bulk result log
    bulkResultLog: bulkActions.bulkResultLog,
    showBulkResultLog: bulkActions.showBulkResultLog,
    setShowBulkResultLog: bulkActions.setShowBulkResultLog,

    // Export
    exporting: exportActions.exporting,

    // Filters
    filters,
    filterOptions,

    // Columns
    columns,
    showColumnDropdown,
    columnDropdownRef,

    // Setters
    setActiveTab: handleTabChange,
    setShowFilters,
    setSelectedLineId,
    setSelectedLineAction,
    setSelectedLineForHold: actions.setSelectedLineForHold,
    setSelectedLineForWorkflow: actions.setSelectedLineForWorkflow,
    setSelectedLineIds: bulkActions.setSelectedLineIds,
    setPutOnHoldModalOpen: actions.setPutOnHoldModalOpen,
    setReleaseHoldModalOpen: actions.setReleaseHoldModalOpen,
    setAcceptModalOpen: actions.setAcceptModalOpen,
    setDeclineModalOpen: actions.setDeclineModalOpen,
    setHoldReason: actions.setHoldReason,
    setShowColumnDropdown,

    // Handlers
    fetchLines,
    patchLine,
    handlePageChange,
    handleFilterChange,
    handleSearch,
    clearFilters,
    handleSort,
    toggleColumn,
    resetColumns,
    selectAllColumns,
    reorderColumns,
    isColumnVisible,
    getColumnConfig,
    handlePutOnHold: actions.handlePutOnHold,
    handleReleaseHold: actions.handleReleaseHold,
    submitPutOnHold: actions.submitPutOnHold,
    submitReleaseHold: actions.submitReleaseHold,
    handleToggleCritical: actions.handleToggleCritical,
    handleWorkflowAction: actions.handleWorkflowAction,
    handleSelectAll: bulkActions.handleSelectAll,
    handleToggleSelect: bulkActions.handleToggleSelect,
    handleBulkAccept: bulkActions.handleBulkAccept,
    handleBulkReject: bulkActions.handleBulkReject,
    handleBulkPutOnHold: bulkActions.handleBulkPutOnHold,
    setShowBulkAcceptModal: bulkActions.setShowBulkAcceptModal,
    setShowBulkRejectModal: bulkActions.setShowBulkRejectModal,
    setShowBulkHoldModal: bulkActions.setShowBulkHoldModal,
    handleExport: exportActions.handleExport,
    handleTemplateExport: exportActions.handleTemplateExport,
    exportTemplates: exportActions.exportTemplates,
    shouldShowActions: actions.shouldShowActions,
    shouldShowDecline: actions.shouldShowDecline,
    shouldShowProposeChange: actions.shouldShowProposeChange,
    getNextAction: actions.getNextAction,
    getSelectableLines: bulkActions.getSelectableLines,
    canSelectForBulkAccept: bulkActions.canSelectForBulkAccept,
  };
}
